import { t } from 'i18next';
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { NotificationPosition } from 'core/enums';
import { useHandleErrors } from 'core/hooks';
import { GetManyResult, GetOneResult, IRequestPayload } from 'core/types';
import { CorporateUserType } from 'corporate/enums';
import { corporateUserService } from 'corporate/services';
import { ICorporateUserAutocomplete } from 'corporate/types';

export const useGetCorporateUsersAutocomplete = (
  requestPayload?: IRequestPayload
): GetManyResult<ICorporateUserAutocomplete> => {
  const { data, isError, isLoading, refetch } = useQuery(
    ['autocompleteUsers', requestPayload],
    () => corporateUserService.getCorporateUsersAutocomplete(requestPayload),
    {
      keepPreviousData: true,
    }
  );

  return {
    records: data?.records ?? [],
    count: data?.count,
    isError,
    isLoading,
    refetch,
  };
};

// Resolves a single customer contact from the foreign-user autocomplete by email
// (the add-in passes the email sender). One email can span multiple contact groups,
// so contactGroupId disambiguates to a deterministic record; falls back to the first
// exact-email match. No exact match -> null (caller no-ops).
export const useGetCustomerContactByEmail = (
  email?: string,
  contactGroupId?: number
): GetOneResult<ICorporateUserAutocomplete | null> => {
  const requestPayload: IRequestPayload = {
    q: email,
    filters: {
      userType: CorporateUserType.CUSTOMER,
      includeEmptyContactGroups: 1,
    },
  };

  const { data, isError, isLoading, isFetched } = useQuery(
    ['customerContactByEmail', email, contactGroupId],
    ({ signal }) =>
      corporateUserService.getCorporateForeignUsersAutocomplete(requestPayload, signal),
    { enabled: !!email }
  );

  const record = useMemo(() => {
    const emailMatches = (data?.records ?? []).filter(
      (user) => user.email?.toLowerCase() === email?.toLowerCase()
    );

    if (!emailMatches.length) {
      return null;
    }

    if (contactGroupId) {
      return (
        emailMatches.find((user) => Number(user.contactGroup?.id) === Number(contactGroupId)) ??
        emailMatches[0]
      );
    }

    return emailMatches[0];
  }, [data, email, contactGroupId]);

  return { record, isError, isLoading, isFetched };
};

export const useCorporateUserActions = () => ({
  ...useChangeUserVersion(),
});

const useChangeUserVersion = () => {
  const queryClient = useQueryClient();

  const { errors } = useHandleErrors({
    success: { message: t('messageCorporateUserVersionChangeSuccess') },
    loading: { message: t('messageCorporateUserVersionChangeInProgress') },
    error: { title: t('errorCorporateUserVersionChange') },
    position: NotificationPosition.TOP,
    invalidateQueries: ['authUser'],
  });

  const { mutateAsync } = useMutation((data) => corporateUserService.changeUserVersion(data), {
    ...errors,
    onSuccess: (data, variables) => {
      errors?.onSuccess(data, variables);

      queryClient.setQueryData('removeLocalStorageLoggedUser', true);
    },
  });

  return { changeUserVersion: mutateAsync };
};
